// ════════════════════════════════════════════════════════════════════════
//  Tastatur und Finger – die Voraussetzung für jede Lektion
//
//  Neun Zeilen, und es werden nie mehr: die Zahl der Finger ist keine
//  Entscheidung der Redaktion. Vorher stand hier je TASTE eine Zeile mit
//  Fingernamen und Farbe – 23 Stück für zwölf Namen, mit dem Ausbau aufs ganze
//  Tastaturfeld wären es neunzig geworden. „linker Zeigefinger“ stand viermal
//  da, und nichts hinderte daran, `f` gelb und `F` grün zu machen.
//
//  Inhalt bleibt die Zuordnung: welche Tasten ein Finger holt. Der Hinweis dazu
//  erscheint an der Stufe, die Ursache liegt hier – seit die Hinweise Links
//  sind, ist der Weg dorthin ein Klick.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle, RowId } from "@otto/lehrgang";
import { formatKeys, parseKeys } from "../../../shared/lib/keys.js";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { NoDraft } from "../../draft/components/NoDraft.js";
import { useAnchorFocus } from "../../../shared/hooks/useAnchorFocus.js";
import { ProblemList } from "../../validation/components/ProblemList.js";
import { problemsAt } from "../../validation/lib/validation.js";
import { anchors } from "../../validation/lib/problemTarget.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./settings.module.css";

/**
 * Namen der Reihen für die Oberfläche.
 *
 * Die Kennung steht im Bundle und ist der Anker, an dem die Lern-App ihre
 * Zusatztasten festmacht; hier braucht es dazu einen Satz, den man liest.
 */
const ROW_NAMES: Record<RowId, string> = {
  zahlen: "Ziffernreihe",
  oben: "Obere Buchstabenreihe",
  grund: "Grundreihe",
  unten: "Untere Buchstabenreihe",
  leer: "Leertastenreihe",
};

export function FingersAndKeyboard() {
  const { bundle, setBundle, problems, readOnly } = useDraft();
  useAnchorFocus();
  if (!bundle) return <NoDraft />;

  const change = (part: Partial<Bundle>) => setBundle({ ...bundle, ...part });
  const changeFinger = (i: number, part: Partial<Bundle["fingers"][number]>) =>
    change({ fingers: bundle.fingers.map((f, j) => (j === i ? { ...f, ...part } : f)) });

  return (
    <>
      <div className={styles.card} id={anchors.fingers}>
        <h1 className={styles.title}>Tastatur und Finger</h1>
        <p className={styles.subtitle}>
          Jedes Zeichen, das in einem Übungstext vorkommt, gehört einem dieser Finger –
          Groß- und Kleinbuchstabe demselben. Die Farbe gehört dem Finger, nicht der
          Taste: das Kind lernt „grün heißt Mittelfinger“.
        </p>

        <ProblemList problems={problemsAt(problems, "fingers")} />

        <table className={own.limit}>
          <thead>
            <tr>
              <th>Finger</th>
              <th>Farbe</th>
              <th>Tasten (mit Komma trennen, ␣ für die Leertaste)</th>
            </tr>
          </thead>
          <tbody>
            {bundle.fingers.map((finger, i) => (
              <tr key={finger.id}>
                <td>
                  <input
                    className={styles.field}
                    value={finger.name}
                    readOnly={readOnly}
                    aria-label={`Name ${finger.id}`}
                    onChange={(e) => changeFinger(i, { name: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="color"
                    className={own.color}
                    value={finger.color}
                    disabled={readOnly}
                    aria-label={`Farbe ${finger.id}`}
                    onChange={(e) =>
                      changeFinger(i, { color: e.target.value.toUpperCase() })
                    }
                  />
                </td>
                <td>
                  <input
                    className={`${styles.field} mono`}
                    value={formatKeys(finger.keys)}
                    readOnly={readOnly}
                    aria-label={`Tasten ${finger.id}`}
                    onChange={(e) => changeFinger(i, { keys: parseKeys(e.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Kein „Finger hinzufügen“: Hände bekommen keine dazu. Neun Zeilen für
            zehn Finger, weil sich die Daumen einen teilen – für die Leertaste
            spielt die Seite keine Rolle. */}
      </div>

      <div className={`${styles.card} ${styles.spaced}`} id={anchors.keyboardRows}>
        <h2 className={own.section}>Tastaturbild</h2>
        <p className={styles.subtitle}>
          Was die Kinder auf dem Bildschirm sehen. Eine Zeile je Tastaturreihe, Zeichen
          mit Komma getrennt. Das Bild zeigt die deutsche Tastatur, nicht nur die
          gelernten Tasten – ungelernte sind grau.
        </p>

        <ProblemList problems={problemsAt(problems, "keyboard.rows")} />

        {bundle.keyboard.rows.map((row) => (
          <label key={row.id} className={`${own.block} ${own.limit}`}>
            {ROW_NAMES[row.id]}
            <input
              className={`${styles.field} mono`}
              value={formatKeys(row.keys)}
              readOnly={readOnly}
              aria-label={ROW_NAMES[row.id]}
              onChange={(e) =>
                change({
                  keyboard: {
                    ...bundle.keyboard,
                    rows: bundle.keyboard.rows.map((r) =>
                      r.id === row.id ? { ...r, keys: parseKeys(e.target.value) } : r,
                    ),
                  },
                })
              }
            />
          </label>
        ))}
      </div>

      <div className={`${styles.card} ${styles.spaced}`} id={anchors.homeRow}>
        <h2 className={own.section}>Grundstellung</h2>
        <p className={styles.subtitle}>
          Die Tasten mit dem farbigen Punkt – dort liegen die Finger, wenn nichts getippt
          wird.
        </p>

        <ProblemList problems={problemsAt(problems, "keyboard.homeRow")} />

        <label className={`${own.block} ${own.limit}`}>
          Tasten mit farbigem Punkt
          <input
            className={`${styles.field} mono`}
            aria-label="Grundstellung"
            readOnly={readOnly}
            value={formatKeys(bundle.keyboard.homeRow.map((t) => t.key))}
            onChange={(e) => {
              const keys = parseKeys(e.target.value);
              change({
                keyboard: {
                  ...bundle.keyboard,
                  homeRow: keys.map((key) => ({
                    key: key,
                    // Immer vom Finger, nicht vom alten Punkt: sonst bliebe
                    // eine Farbe stehen, die zu ihrem Finger nicht mehr passt.
                    color:
                      bundle.fingers.find((f) => f.keys.includes(key))?.color ??
                      "#8CA3B5",
                  })),
                },
              });
            }}
          />
          <span className={own.note}>
            Die Farbe kommt automatisch vom Finger der Taste.
          </span>
        </label>
      </div>
    </>
  );
}
